"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import CustomInput from "./CustomInput";
import { forgottenFormSchema } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { getForgottenPassword } from "@/lib/actions/user.actions";

const ForgottenPassword = ({ domain_configuration }: { domain_configuration: string|undefined }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const router = useRouter();

  const is_labelleconcierge = domain_configuration === "labelleconciergerie";

  const formSchema = forgottenFormSchema();

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      email: "",
    },
  });

  // 2. Define a submit handler.
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setError("");
    setSuccess("");
    setIsLoading(true);
    try {
      const parsed_username =
        "33" +
        data.username.replaceAll(" ", "").replaceAll(".", "").replace(/^0/, "");
      const userData = {
        username: parsed_username,
        email: data.email,
      };
      const response = await getForgottenPassword(userData);
      console.log('forgottenPassword/onSubmit/response', response);
      if (!response) {
        setError("Une erreur de connection s'est produite. Veuillez réessayer !");
      } else if (response.error) {
        setError(response.error);
      } else if (response.data.message) {
        setSuccess(response.data.message);
      }else{
        console.error('forgottenPassword/onSubmit/no reponse', response);
        setError("Une erreur de connection s'est produite. Veuillez nous contacter !");
      }
      console.log(data);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="auth-form">
      <header className="flex flex-col gap-5 md:gap-8">
        <Link href={`${is_labelleconcierge ? "https://www.labelleconciergerie.com/": "https://www.taxisconnect.com/"}`} className="flex cursor-pointer items-center gap-1">
          <Image
            src={`${is_labelleconcierge ? "/icons/labelleconciergerie_icon.png": "/icons/icon@2x.png"}`}
            alt="Horizon Logo"
            width={34}
            height={40}
            className="rounded-md"
          />
          <h1 className="text-26 font-ibm-plex-serif font-bold text-black-1 ml-2">
            
            {is_labelleconcierge ? "La belle conciergerie": "TaxisConnect"}
          </h1>
        </Link>
              <div className="text-black-1 text-2xl font-bold">Mot de passe oublié ?</div>
      </header>
      <div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <CustomInput
              control={form.control}
              name={"username"}
              label={"Téléphone"}
              placeholder={"Indiquez votre numéro de téléphone"}
            />
            <CustomInput
              control={form.control}
              name={"email"}
              label={"Email"}
              placeholder={"Indiquez votre mot de email de contact"}
            />
            {success && (
              <div className="text-green-500 text-sm font-medium">{success}</div>
            )}
            {error && (
              <div className="text-red-500 text-sm font-medium">{error}</div>
            )}

            <div className="flex flex-col gap-4">
              <Button type="submit" className="form-btn" disabled={isLoading}>
                {isLoading ? (
                  <div className="flex">
                    <Loader2 size={20} className="animate-spin" /> &nbsp;
                    Loading...
                  </div>
                ) : (
                  "Envoyer mon mot de passe"
                )}
              </Button>
            </div>
          </form>
        </Form>
        <footer className="flex justify-center gap-1 flex-col">
          <div className="text-16 font-normal text-gray-600">
            Pour nous joindre :{" "}
            <a href="mailto:commercial@taxisconnect.com" className="underline">
            {is_labelleconcierge ? "contact@labelleconciergerie.com": "commercial@taxisconnect.com"}
            </a>{" "}
            ou {is_labelleconcierge ? "09 75 128 128": "09 75 120 120"} (appel local) aux heures de bureau
          </div>
          <Link href={"/sign-in"}>
            <p className="text-16 font-semibold text-blue-600">
              Se connecter
            </p>
          </Link>
        </footer>
      </div>
    </section>
  );
};

export default ForgottenPassword;
